import { Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Check, Copy, ExternalLink, Inbox, MapPin } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import apiClient from '@/api/client'
import { cn } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'
import { AdminStorefrontAppearance } from '@/components/admin/AdminStorefrontAppearance'
import { AdminStorefrontHours } from '@/components/admin/AdminStorefrontHours'
import { AdminStorefrontFeatured } from '@/components/admin/AdminStorefrontFeatured'
import { AdminStorefrontContact } from '@/components/admin/AdminStorefrontContact'
import { AdminStorefrontApp } from '@/components/admin/AdminStorefrontApp'
import { AdminStorefrontFeatures } from '@/components/admin/AdminStorefrontFeatures'

export type WebsiteTab = 'design' | 'features' | 'hours' | 'featured' | 'contact' | 'app'

/**
 * Admin → Website.
 *
 * The restaurant's own ordering site, as a first-class page rather than a
 * tab inside Settings: where it lives and how to share it, then its design
 * (a studio with a live preview that fills the window), its opening hours,
 * the dishes it features, and its branches and contact details. Gated by the online-ordering add-on at the route.
 */
export function AdminWebsite({ tab, onTab }: { tab: WebsiteTab; onTab: (t: WebsiteTab) => void }) {
  const { toast } = useToast()
  const settingsQuery = useQuery({ queryKey: ['settings', 'all'], queryFn: () => apiClient.getAllSettings() })
  const d = settingsQuery.data?.data as Record<string, unknown> | undefined
  const raw = typeof d?.storefront_base_url === 'string' ? d.storefront_base_url.trim() : ''
  const address = /^https?:\/\//i.test(raw) ? raw.replace(/\/+$/, '') : ''
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(address)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1600)
    } catch {
      toast({ title: 'Could not copy', description: address, variant: 'destructive' })
    }
  }

  // The design studio fills the window and scrolls inside itself; the other
  // tabs are ordinary pages.
  const studio = tab === 'design'

  return (
    <div className={cn('flex flex-col gap-5 p-6', studio && 'lg:h-full lg:min-h-[44rem]')}>
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-[16rem] flex-1">
          <h1 className="text-3xl font-bold tracking-tight">Website</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            The site your customers order from. Orders placed there arrive in your Online Orders inbox.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 lg:shrink-0">
          {address && (
            <>
              <span className="inline-flex min-w-0 max-w-[16rem] items-center gap-2 rounded-md border bg-muted/40 px-3 py-1.5 text-sm">
                <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" aria-hidden />
                <span className="truncate font-medium">{address.replace(/^https?:\/\//, '')}</span>
              </span>
              <Button type="button" variant="outline" size="sm" onClick={copy} className="gap-1.5">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copied' : 'Copy link'}
              </Button>
              <Button asChild size="sm" className="gap-1.5">
                <a href={address} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" /> View site
                </a>
              </Button>
              <span className="mx-1 hidden h-5 w-px bg-border sm:block" aria-hidden />
            </>
          )}
          <Button asChild variant="ghost" size="sm" className="gap-1.5">
            <Link to="/admin/online-orders">
              <Inbox className="h-4 w-4" /> Online orders
            </Link>
          </Button>
          <Button asChild variant="ghost" size="sm" className="gap-1.5">
            <Link to="/admin/delivery-zones">
              <MapPin className="h-4 w-4" /> Delivery zones
            </Link>
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => onTab(v as WebsiteTab)} className="flex min-h-0 flex-1 flex-col gap-5">
        <TabsList className="h-auto self-start rounded-lg bg-muted/60 p-1">
          <TabsTrigger value="design" className="rounded-md px-4 py-2">
            Design
          </TabsTrigger>
          <TabsTrigger value="features" className="rounded-md px-4 py-2">
            Features
          </TabsTrigger>
          <TabsTrigger value="hours" className="rounded-md px-4 py-2">
            Opening hours
          </TabsTrigger>
          <TabsTrigger value="featured" className="rounded-md px-4 py-2">
            Featured dishes
          </TabsTrigger>
          <TabsTrigger value="contact" className="rounded-md px-4 py-2">
            Contact &amp; locations
          </TabsTrigger>
          <TabsTrigger value="app" className="rounded-md px-4 py-2">
            App
          </TabsTrigger>
        </TabsList>
        <TabsContent value="design" className="mt-0 min-h-0 flex-1">
          <AdminStorefrontAppearance />
        </TabsContent>
        <TabsContent value="features" className="mt-0 max-w-3xl">
          <AdminStorefrontFeatures />
        </TabsContent>
        <TabsContent value="hours" className="mt-0 max-w-3xl">
          <AdminStorefrontHours />
        </TabsContent>
        <TabsContent value="featured" className="mt-0 max-w-4xl">
          <AdminStorefrontFeatured />
        </TabsContent>
        <TabsContent value="contact" className="mt-0 max-w-3xl">
          <AdminStorefrontContact />
        </TabsContent>
        <TabsContent value="app" className="mt-0 max-w-5xl">
          <AdminStorefrontApp />
        </TabsContent>
      </Tabs>
    </div>
  )
}
